// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useMemo, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { documentsApi, analysisHistoryApi } from "@/api"
import { handleApiError } from "@/api"
import type { Document } from "@/api"
import type { AnalysisDetail } from "@/api/analysis-history"
import { format } from "date-fns"
import { formatInTimeZone } from "date-fns-tz"
import { useAuth } from "@umd-dit-ai-workspace/workspaces-component"
import { JobPoller, JobStatus } from "@umd-dit-ai-workspace/jobs-component"
import { configApi } from "@/api/config"
import { PromptsList } from "@/components/PromptsList"
import { WorkspaceRequiredGuard } from "@/components/WorkspaceRequiredGuard"
import { AnalysisReport } from "@/components/AnalysisReport"
import { AnalysisHistoryTable } from "@/components/AnalysisHistoryTable"
import { extractPromptName, extractAnalysisType } from "@/utils/prompts"
import { exportToAccessiblePDF } from "@/utils/accessiblePdfExport"
import { CheckCircle, FileText, Folder, FolderPlus, File, Eye, Download, AlertTriangle, AlertCircle, X } from "lucide-react"
import IndividualAnalysisTray, { type AnalysisResult } from "@/components/IndividualAnalysisTray"
import { ConfirmDialog } from "@/components/ConfirmDialog"

export default function AnalysisPage() {
  // Tab state
  const [activeTab, setActiveTab] = useState<'create' | 'history'>('create')
  
  // Create analysis states
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([])
  const [analysisResult, setAnalysisResult] = useState<any>(null)
  const [analyzingDocuments, setAnalyzingDocuments] = useState<Document[]>([])
  const [promptType, setPromptType] = useState<'default' | 'workspace'>('default')
  const [selectedPrompt, setSelectedPrompt] = useState<any>(null)
  const [templateType, setTemplateType] = useState<'default' | 'workspace'>('default')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedFolder, setSelectedFolder] = useState<string>('all')
  const [sortColumn, setSortColumn] = useState<'filename' | 'type' | 'size' | 'status' | 'uploaded' | 'analyzed_by' | 'analyzed_date'>('uploaded')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [downloadingPdfFor, setDownloadingPdfFor] = useState<string | null>(null)
  
  // History tab states
  const [selectedAnalysisDetail, setSelectedAnalysisDetail] = useState<AnalysisDetail | null>(null)
  const [viewingAnalysisId, setViewingAnalysisId] = useState<string | null>(null)
  const [downloadingAnalysisPdfFor, setDownloadingAnalysisPdfFor] = useState<string | null>(null)
  
  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [analysisToDelete, setAnalysisToDelete] = useState<string | null>(null)
  
  // Notification banner state
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null)

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [notification])
  
  // Token warning and individual analysis mode states
  const [showTokenWarning, setShowTokenWarning] = useState(false)
  const [individualAnalysisMode, setIndividualAnalysisMode] = useState(false)
  const [individualAnalyses, setIndividualAnalyses] = useState<Map<string, AnalysisResult>>(new Map())
  const [isAnalyzingIndividually, setIsAnalyzingIndividually] = useState(false)
  const [analysisJobIds, setAnalysisJobIds] = useState<Map<string, string>>(new Map())


  const queryClient = useQueryClient()

  // Get authentication context for org_id and ws_id
  const { selectedOrganizationId, selectedWorkspaceId, userId, user, isAdmin } = useAuth()

  // Query for workspace configuration to get the default LLM model and member defaults
  const { data: workspaceConfig } = useQuery({
    queryKey: ['workspaceConfig', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Organization and workspace must be selected')
      }
      return configApi.getWorkspaceConfig(selectedOrganizationId, selectedWorkspaceId)
    },
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Token limit constant
  const SAFE_TOKEN_LIMIT = 100000
  
  // Determine if bulk analysis based on selection count
  const isBulkAnalysis = selectedDocuments.length > 1

  // Query for analysis history
  const { data: analysesData, isLoading: analysesLoading, refetch: refetchAnalyses } = useQuery({
    queryKey: ['analyses', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Organization and workspace must be selected')
      }
      return analysisHistoryApi.listAnalyses(selectedOrganizationId, selectedWorkspaceId, 100, undefined, 'bulk analyzer')
    },
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
    refetchInterval: activeTab === 'history' ? 10000 : false,
  })

  // Query for documents list - only run if we have the required parameters
  const { data: documents = [], isLoading: documentsLoading } = useQuery<Document[]>({
    queryKey: ['documents', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Organization and workspace must be selected')
      }
      return documentsApi.getDocuments(selectedOrganizationId, selectedWorkspaceId)
    },
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
    refetchInterval: 5000,
  })

  // Check if any selected document is "other" type — triggers different prompt set and hides templates
  const hasOtherDocument = selectedDocuments.some(docId =>
    documents.find(d => d.document_id === docId)?.document_type === 'other'
  )

  // Auto-apply member defaults if user is not admin
  const effectivePromptType = !isAdmin && workspaceConfig
    ? (hasOtherDocument
        ? workspaceConfig.member_other_doc_prompt_type
        : isBulkAnalysis
          ? workspaceConfig.member_bulk_doc_prompt_type
          : workspaceConfig.member_single_doc_prompt_type) || promptType
    : promptType

  const effectivePromptName = !isAdmin && workspaceConfig
    ? (hasOtherDocument
        ? workspaceConfig.member_other_doc_prompt_name
        : isBulkAnalysis
          ? workspaceConfig.member_bulk_doc_prompt_name
          : workspaceConfig.member_single_doc_prompt_name) || null
    : selectedPrompt?.name || null

  const effectivePromptAnalysisType = !isAdmin && workspaceConfig
    ? (hasOtherDocument
        ? workspaceConfig.member_other_doc_prompt_analysis_type
        : isBulkAnalysis
          ? workspaceConfig.member_bulk_doc_prompt_analysis_type
          : workspaceConfig.member_single_doc_prompt_analysis_type) || selectedPrompt?.analysisType
    : selectedPrompt?.analysisType

  const effectiveTemplateType = !isAdmin && workspaceConfig
    ? (isBulkAnalysis ? workspaceConfig.member_bulk_doc_template_type : workspaceConfig.member_single_doc_template_type) || templateType
    : templateType

  const effectiveTemplateName = !isAdmin && workspaceConfig
    ? (isBulkAnalysis ? workspaceConfig.member_bulk_doc_template_name : workspaceConfig.member_single_doc_template_name) || null
    : selectedTemplate

  // Check if member has required defaults configured
  const memberHasRequiredDefaults = !isAdmin && workspaceConfig
    ? hasOtherDocument
      ? Boolean(workspaceConfig.member_other_doc_prompt_name)
      : isBulkAnalysis
        ? Boolean(workspaceConfig.member_bulk_doc_prompt_name && workspaceConfig.member_bulk_doc_template_name)
        : Boolean(workspaceConfig.member_single_doc_prompt_name && workspaceConfig.member_single_doc_template_name)
    : true // Admins always have access

  // Calculate total tokens for selected documents
  const calculateTotalTokens = useMemo(() => {
    if (selectedDocuments.length < 2) return 0
    
    return selectedDocuments.reduce((total, docId) => {
      const doc = documents.find(d => d.document_id === docId)
      return total + (doc?.estimated_token_count || 0)
    }, 0)
  }, [selectedDocuments, documents])

  // Check token limits and show warning for multi-document analysis
  useEffect(() => {
    if (individualAnalysisMode) {
      setShowTokenWarning(false)
      return
    }

    if (selectedDocuments.length >= 2) {
      const totalTokens = calculateTotalTokens
      
      if (totalTokens > SAFE_TOKEN_LIMIT) {
        setShowTokenWarning(true)
      } else {
        setShowTokenWarning(false)
      }
    } else {
      setShowTokenWarning(false)
    }
  }, [selectedDocuments, individualAnalysisMode, calculateTotalTokens, SAFE_TOKEN_LIMIT])

  // Query for available prompts based on selected prompt type
  // Filter prompts based on number of selected documents:
  // - 1 document: show 'analyzer' prompts
  // - 2+ documents: show 'bulk_analyzer' prompts
  const { data: availablePrompts = [], isLoading: promptsLoading } = useQuery<string[]>({
    queryKey: ['prompts', promptType, selectedOrganizationId, selectedWorkspaceId, selectedDocuments.length, hasOtherDocument, 'analyzer-filter'],
    queryFn: async () => {
      let allPrompts: string[] = []
      if (promptType === 'default') {
        allPrompts = await configApi.getPrompts('default')
      } else {
        if (!selectedOrganizationId || !selectedWorkspaceId) {
          throw new Error('Organization and workspace must be selected for workspace prompts')
        }
        allPrompts = await configApi.getPrompts('workspace', selectedOrganizationId, selectedWorkspaceId)
      }
      console.log('All prompts fetched:', allPrompts)
      
      // Filter based on document type and selection count
      const isMultiDoc = selectedDocuments.length >= 2
      const filterPrefix = hasOtherDocument ? 'other/' : (isMultiDoc ? 'bulk analyzer/' : 'analyzer/')
      const filtered = allPrompts.filter(prompt => prompt.startsWith(filterPrefix))
      console.log(`Filtered prompts (prefix: ${filterPrefix}):`, filtered)
      return filtered
    },
    enabled: Boolean(
      promptType === 'default' ||
      (promptType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  })

  // Query for available templates based on selected template type
  const { data: availableTemplates = [], isLoading: templatesLoading } = useQuery<string[]>({
    queryKey: ['templates', templateType, selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      if (templateType === 'default') {
        return configApi.getTemplates('default')
      } else {
        if (!selectedOrganizationId || !selectedWorkspaceId) {
          throw new Error('Organization and workspace must be selected for workspace templates')
        }
        return configApi.getTemplates('workspace', selectedOrganizationId, selectedWorkspaceId)
      }
    },
    enabled: Boolean(
      templateType === 'default' ||
      (templateType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  })

  const analyzeMutation = useMutation({
    mutationFn: async (input: Document[] | { docs: Document[], analysisSummaries: any[] }) => {
      // Handle both old format (Document[]) and new format ({ docs, analysisSummaries })
      const docs = Array.isArray(input) ? input : input.docs
      const analysisSummaries = Array.isArray(input) ? undefined : input.analysisSummaries
      
      const mode = analysisSummaries ? 'SUMMARY-BASED' : 'RAW DOCUMENT'
      console.log(`Starting ${mode} analysis for ${docs.length} document(s)`)

      if (!selectedOrganizationId || !selectedWorkspaceId || !userId) {
        throw new Error('Missing required parameters: organization, workspace, or user ID')
      }
      setAnalyzingDocuments(docs)
      setJobStatus('PENDING')

      // For members, use effective values from workspace config; for admins, use selected values
      const promptName = effectivePromptName || '';
      // For multi-doc "other" type analyses, always record as 'bulk analyzer' so they
      // appear in the Analysis History tab (which filters by 'bulk analyzer').
      const analysisType = (hasOtherDocument && isBulkAnalysis)
        ? 'bulk analyzer'
        : effectivePromptAnalysisType || undefined;
      const username = user?.userInfo?.username || user?.name || userId;
      const usedPromptType = effectivePromptType;
      const usedTemplateType = effectiveTemplateType;
      const usedTemplateName = effectiveTemplateName || undefined;

      // Choose endpoint based on number of documents
      let result
      if (docs.length === 1) {
        // Single document analysis
        result = await documentsApi.analyzeDocument(
          docs[0].document_id,
          selectedOrganizationId,
          selectedWorkspaceId,
          userId,
          username,
          usedPromptType,
          promptName || undefined,
          analysisType,
          usedTemplateType,
          usedTemplateName,
          workspaceConfig?.default_llm_model
        )
        console.log('Single analysis job queued:', result)
        return { ...result, documentIds: [docs[0].document_id] }
      } else {
        // Multi-document analysis
        const documentIds = docs.map(d => d.document_id)
        
        // When using summaries, override prompt settings for summary-based analysis
        const finalPromptName = analysisSummaries ? 'threshold_exceeded_bulk_analyzer_prompt' : (promptName || undefined)
        const finalAnalysisType = analysisSummaries ? 'bulk analyzer' : analysisType
        const finalTemplateName = analysisSummaries ? 'bulk_analysis_schema' : usedTemplateName
        
        result = await documentsApi.multiAnalyzeDocuments(
          documentIds,
          selectedOrganizationId,
          selectedWorkspaceId,
          userId,
          username,
          usedPromptType,
          finalPromptName,
          finalAnalysisType,
          usedTemplateType,
          finalTemplateName,
          workspaceConfig?.default_llm_model,
          analysisSummaries  // Pass summaries for summary-based analysis
        )
        console.log('Multi-analysis job queued:', result)
        return { ...result, documentIds }
      }
    },
    onSuccess: (data) => {
      const jobId = data.job_id
      setCurrentJobId(jobId)
      // Invalidate analyses query so the "processing" status shows immediately in history
      queryClient.invalidateQueries({ queryKey: ['analyses'] })
    },
    onError: (error) => {
      setAnalyzingDocuments([])
      setCurrentJobId(null)
      setJobStatus(null)
      handleApiError(error)
    }
  })

  // Query for folders list (extract unique folders from documents)
  const folders = useMemo(() => {
    const uniqueFolders = new Set<string>()
    documents.forEach(doc => {
      if (doc.folder) {
        uniqueFolders.add(doc.folder)
      }
    })
    return Array.from(uniqueFolders).sort()
  }, [documents])

  // Get folder counts
  const getFolderCount = (folder: string) => {
    return documents.filter(doc => doc.folder === folder).length
  }

  // Get all documents (both uploaded and completed can be analyzed)
  const allDocuments = documents


  // Filter documents based on folder and search query
  const filteredDocuments = allDocuments.filter(doc => {
    const matchesFolder = selectedFolder === 'all' || doc.folder === selectedFolder
    const matchesSearch = searchQuery === '' || 
      doc.original_filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.document_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.processing_status.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.folder || '').toLowerCase().includes(searchQuery.toLowerCase())
    return matchesFolder && matchesSearch
  })

  // Sort documents
  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    let aValue: any, bValue: any

    switch (sortColumn) {
      case 'filename':
        aValue = a.original_filename.toLowerCase()
        bValue = b.original_filename.toLowerCase()
        break
      case 'type':
        aValue = a.document_type.toLowerCase()
        bValue = b.document_type.toLowerCase()
        break
      case 'size':
        aValue = a.file_size_bytes
        bValue = b.file_size_bytes
        break
      case 'status':
        aValue = a.processing_status.toLowerCase()
        bValue = b.processing_status.toLowerCase()
        break
      case 'uploaded':
        aValue = new Date(a.uploaded_at).getTime()
        bValue = new Date(b.uploaded_at).getTime()
        break
      case 'analyzed_by':
        aValue = (a.analysis_data?.document_metadata?.analyzed_by || a.uploaded_by_username || a.uploaded_by || '').toLowerCase()
        bValue = (b.analysis_data?.document_metadata?.analyzed_by || b.uploaded_by_username || b.uploaded_by || '').toLowerCase()
        break
      case 'analyzed_date':
        aValue = a.analysis_data?.document_metadata?.analysis_timestamp ? new Date(a.analysis_data.document_metadata.analysis_timestamp).getTime() : 0
        bValue = b.analysis_data?.document_metadata?.analysis_timestamp ? new Date(b.analysis_data.document_metadata.analysis_timestamp).getTime() : 0
        break
      default:
        return 0
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  const handleSort = (column: typeof sortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ column }: { column: typeof sortColumn }) => {
    if (sortColumn !== column) {
      return <span className="text-blue-600">⇅</span>
    }
    return <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
  }

  // Pagination logic
  const totalDocuments = sortedDocuments.length
  const totalPages = Math.ceil(totalDocuments / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedDocuments = sortedDocuments.slice(startIndex, endIndex)

  // Reset to page 1 when search query or page size changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setCurrentPage(1)
  }

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1))
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploaded': return 'bg-blue-100 text-blue-800'
      case 'processing': return 'bg-yellow-100 text-yellow-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'uploaded': return 'Ready'
      case 'processing': return 'Processing'
      case 'completed': return 'Analyzed'
      case 'failed': return 'Failed'
      default: return status
    }
  }

  // Check token limits and show warning for multi-document analysis
  useEffect(() => {
    if (individualAnalysisMode) {
      setShowTokenWarning(false)
      return
    }

    if (selectedDocuments.length >= 2) {
      const totalTokens = calculateTotalTokens
      
      if (totalTokens > SAFE_TOKEN_LIMIT) {
        setShowTokenWarning(true)
      } else {
        setShowTokenWarning(false)
      }
    } else {
      setShowTokenWarning(false)
    }
  }, [selectedDocuments, individualAnalysisMode, calculateTotalTokens])

  // Start individual analysis for all selected documents
  const startIndividualAnalysis = async () => {
    if (!selectedOrganizationId || !selectedWorkspaceId || !userId) return

    setIndividualAnalysisMode(true)
    setIsAnalyzingIndividually(true)
    setShowTokenWarning(false)

    const newAnalyses = new Map<string, AnalysisResult>()

    selectedDocuments.forEach(docId => {
      const doc = allDocuments.find(d => d.document_id === docId)
      if (doc) {
        if (doc.analysis_data) {
          newAnalyses.set(docId, {
            documentId: docId,
            documentName: doc.original_filename,
            status: 'completed',
            analysisData: {
              ...doc.analysis_data,
              document_type: doc.document_type
            },
            tokenCount: doc.estimated_token_count
          })
          console.log(`✅ Found existing analysis for ${doc.original_filename}`)
        } else {
          newAnalyses.set(docId, {
            documentId: docId,
            documentName: doc.original_filename,
            status: 'pending'
          })
        }
      }
    })

    setIndividualAnalyses(newAnalyses)

    try {
      for (const docId of selectedDocuments) {
        const doc = allDocuments.find(d => d.document_id === docId)
        if (doc && !doc.analysis_data) {
          await analyzeDocumentIndividually(docId, newAnalyses, setIndividualAnalyses)
        }
      }
      console.log('✅ All documents analyzed successfully')
    } catch (err) {
      console.error('Error during individual analysis:', err)
      handleApiError(err)
    } finally {
      setIsAnalyzingIndividually(false)
    }
  }

  // Analyze a single document with unified analysis prompt
  const analyzeDocumentIndividually = async (
    docId: string, 
    analysesMap: Map<string, AnalysisResult>,
    setAnalysesMap: React.Dispatch<React.SetStateAction<Map<string, AnalysisResult>>>
  ) => {
    try {
      // Update status to processing
      setAnalysesMap(prev => {
        const next = new Map(prev)
        const current = next.get(docId)
        if (current) {
          next.set(docId, { ...current, status: 'processing' })
        }
        return next
      })

      // Start analysis job with unified analysis prompt
      const username = user?.userInfo?.username || user?.name || userId;
      const result = await documentsApi.analyzeDocument(
        docId,
        selectedOrganizationId!,
        selectedWorkspaceId!,
        userId!,
        username,
        'default', // Use default prompt type
        'general_analysis_prompt', // Prompt name for initial unified analysis
        'analyzer', // Analysis type subdirectory
        undefined, // template_type (optional)
        undefined, // template_name (optional)
        workspaceConfig?.default_llm_model
      )

      const jobId = result.job_id

      // Store job ID for tracking
      setAnalysisJobIds(prev => {
        const next = new Map(prev)
        next.set(docId, jobId)
        return next
      })

      // Poll for job completion
      await pollIndividualAnalysisJob(docId, jobId, setAnalysesMap)

    } catch (err) {
      console.error(`Failed to analyze document ${docId}:`, err)
      setAnalysesMap(prev => {
        const next = new Map(prev)
        const current = next.get(docId)
        if (current) {
          next.set(docId, { 
            ...current, 
            status: 'failed',
            error: err instanceof Error ? err.message : 'Analysis failed'
          })
        }
        return next
      })
    }
  }

  // Poll for individual analysis job completion
  const pollIndividualAnalysisJob = async (
    docId: string,
    jobId: string,
    setAnalysesMap: React.Dispatch<React.SetStateAction<Map<string, AnalysisResult>>>
  ): Promise<void> => {
    return new Promise((resolve) => {
      const checkStatus = async () => {
        try {
          // Get document to check if analysis is complete
          const docs = await documentsApi.getDocuments(selectedOrganizationId!, selectedWorkspaceId!)
          const doc = docs.find(d => d.document_id === docId)

          if (doc?.analysis_data) {
            // Analysis complete
            setAnalysesMap(prev => {
              const next = new Map(prev)
              const current = next.get(docId)
              if (current) {
                next.set(docId, {
                  ...current,
                  status: 'completed',
                  analysisData: {
                    ...doc.analysis_data,
                    document_type: doc.document_type
                  },
                  tokenCount: doc.estimated_token_count
                })
              }
              return next
            })
            resolve()
          } else {
            // Still processing, check again in 2 seconds
            setTimeout(checkStatus, 2000)
          }
        } catch (err) {
          console.error(`Error polling job ${jobId}:`, err)
          setAnalysesMap(prev => {
            const next = new Map(prev)
            const current = next.get(docId)
            if (current) {
              next.set(docId, {
                ...current,
                status: 'failed',
                error: 'Failed to retrieve analysis results'
              })
            }
            return next
          })
          resolve()
        }
      }

      checkStatus()
    })
  }

  // Analyze all documents after individual unified analysis is complete
  const analyzeFromUnifiedSummaries = () => {
    // Get all completed analyses
    const completedAnalyses = Array.from(individualAnalyses.values())
      .filter(a => a.status === 'completed' && a.analysisData)

    if (completedAnalyses.length === 0) {
      handleApiError(new Error('No completed analyses to process. Please wait for analyses to complete.'))
      return
    }

    // Extract analysis summaries from completed analyses
    const analysisSummaries = completedAnalyses.map(a => {
      // Extract the analysis data (could be nested in result.analysis_data or directly)
      const analysisData = a.analysisData?.result?.analysis_data || a.analysisData
      return analysisData
    })

    // Get the documents that have been analyzed
    const documentIds = completedAnalyses.map(a => a.documentId)
    const docs = allDocuments.filter(d => documentIds.includes(d.document_id))
    
    if (docs.length > 0) {
      // Pass the summaries to mutation - this will trigger summary-based analysis
      analyzeMutation.mutate({ docs, analysisSummaries })
    }
  }

  // Handle job completion
  const handleJobComplete = async () => {
    console.log(`Job ${currentJobId} completed, fetching document(s)`)

    if (analyzingDocuments.length === 0) {
      console.error('No documents being analyzed')
      setAnalyzingDocuments([])
      setCurrentJobId(null)
      setJobStatus('ERROR')
      return
    }

    if (analyzingDocuments.length === 1) {
      // Single document analysis - data is stored in the document record
      const updatedDocs = await documentsApi.getDocuments(
        selectedOrganizationId!,
        selectedWorkspaceId!
      )
      
      const analyzedDoc = updatedDocs.find(d => d.document_id === analyzingDocuments[0].document_id)
      if (analyzedDoc && analyzedDoc.analysis_data) {
        console.log('Single-doc analysis data retrieved:', analyzedDoc.analysis_data)
        const username = user?.userInfo?.username || user?.name || userId;
        setAnalysisResult({
          status: 'completed',
          filename: analyzedDoc.original_filename,
          document_type: analyzedDoc.document_type,
          duration: 0,
          promptName: analyzedDoc.analysis_data.promptName || selectedPrompt?.name,
          analysisType: analyzedDoc.analysis_data.analysisType || selectedPrompt?.analysisType,
          promptType: analyzedDoc.analysis_data.promptType || promptType,
          templateName: analyzedDoc.analysis_data.templateName || selectedTemplate,
          templateType: analyzedDoc.analysis_data.templateType || templateType,
          analyzedBy: username,
          analysisDate: analyzedDoc.analysis_data.document_metadata?.analysis_timestamp,
          ...analyzedDoc.analysis_data
        })
        
        // Clear analyzing state immediately after setting result for single-doc
        setAnalyzingDocuments([])
        setCurrentJobId(null)
        setJobStatus(null)
      } else {
        console.error('Document not found or no analysis data')
        setJobStatus('ERROR')
        setAnalyzingDocuments([])
        setCurrentJobId(null)
      }
    } else {
      // Multi-document analysis - data is stored in analysis_history, NOT in document records
      // We need to fetch the most recent analysis from analysis history
      try {
        console.log('Fetching multi-doc analysis from analysis history...')
        
        // Refresh analyses to get the latest
        await queryClient.invalidateQueries({ queryKey: ['analyses'] })
        const analysesData = await analysisHistoryApi.listAnalyses(
          selectedOrganizationId!,
          selectedWorkspaceId!,
          10,  // Get last 10 analyses
          'completed'  // Only completed ones
        )
        
        // Find the most recent completed analysis that matches our document IDs
        const documentIds = analyzingDocuments.map(d => d.document_id)
        
        // Filter all analyses that match the document IDs
        const matchingAnalyses = analysesData.analyses.filter(analysis => {
          // Check if this analysis has the same document IDs
          const analysisDocIds = analysis.document_ids || []
          return analysisDocIds.length === documentIds.length &&
                 analysisDocIds.every(id => documentIds.includes(id))
        })
        
        // Sort by completed_at timestamp (most recent first) and pick the first one
        const matchingAnalysis = matchingAnalyses.sort((a, b) => {
          const timeA = a.completed_at ? new Date(a.completed_at).getTime() : 0
          const timeB = b.completed_at ? new Date(b.completed_at).getTime() : 0
          return timeB - timeA // Descending order (most recent first)
        })[0]
        
        if (matchingAnalysis) {
          console.log('Found matching analysis in history:', matchingAnalysis.analysis_id)
          
          // Fetch the full analysis with results
          const detail = await analysisHistoryApi.getAnalysis(
            selectedOrganizationId!,
            selectedWorkspaceId!,
            matchingAnalysis.analysis_id,
            true
          )
          
          if (detail.analysis_data) {
            console.log('Multi-doc analysis data retrieved from history:', detail.analysis_data)
            const username = user?.userInfo?.username || user?.name || userId;
            const filenamesArray = analyzingDocuments.map(d => d.original_filename)
            const documentTypesArray = analyzingDocuments.map(d => d.document_type)
            const filenames = filenamesArray.join(', ')

            // Ensure document_metadata includes the filenames array
            const enhancedAnalysisData = {
              ...detail.analysis_data,
              document_metadata: {
                ...detail.analysis_data.document_metadata,
                filenames: filenamesArray,
                document_types: documentTypesArray
              }
            };
            
            setAnalysisResult({
              status: 'completed',
              filename: filenames,
              document_type: 'bulk_analysis',
              duration: 0,
              promptName: detail.analysis_data.promptName || detail.prompt_name,
              analysisType: detail.analysis_data.analysisType || detail.analysis_type,
              promptType: detail.analysis_data.promptType || detail.prompt_type,
              templateName: detail.analysis_data.templateName || detail.template_name,
              templateType: detail.analysis_data.templateType || detail.template_type,
              analyzedBy: detail.created_by || username,
              analysisDate: detail.completed_at,
              documentCount: analyzingDocuments.length,
              ...enhancedAnalysisData
            })
            
            // Clear analyzing state immediately after setting result for multi-doc
            setAnalyzingDocuments([])
            setCurrentJobId(null)
            setJobStatus(null)
          } else {
            console.error('Multi-document analysis found but no analysis_data')
            setJobStatus('ERROR')
            setAnalyzingDocuments([])
            setCurrentJobId(null)
          }
        } else {
          console.error('Multi-document analysis not found in history')
          setJobStatus('ERROR')
          setAnalyzingDocuments([])
          setCurrentJobId(null)
        }
      } catch (error) {
        console.error('Error fetching multi-document analysis from history:', error)
        setJobStatus('ERROR')
        setAnalyzingDocuments([])
        setCurrentJobId(null)
      }
    }
    
    queryClient.invalidateQueries({ queryKey: ['documents'] })
    queryClient.invalidateQueries({ queryKey: ['analyses'] })
  }

  // Handle job error
  const handleJobError = (error: Error) => {
    console.error('Analysis job failed:', error)
    setAnalyzingDocuments([])
    setCurrentJobId(null)
    setJobStatus('ERROR')
    handleApiError(error)
  }

  // Reset individual analysis mode
  const resetIndividualAnalysisMode = () => {
    setIndividualAnalysisMode(false)
    setIndividualAnalyses(new Map())
    setIsAnalyzingIndividually(false)
    setAnalysisJobIds(new Map())
    setShowTokenWarning(false)
  }

  // Handle direct accessible PDF download from table
  const handleDownloadPDF = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!doc.analysis_data) {
      alert('No analysis data available for this document');
      return;
    }

    setDownloadingPdfFor(doc.document_id);

    try {
      // Create analysis data object for PDF generation
      const analysisData = {
        status: 'completed',
        filename: doc.original_filename,
        original_filename: doc.original_filename,
        document_type: doc.document_type,
        documentType: doc.document_type,
        duration: 0,
        promptName: doc.analysis_data.promptName || doc.analysis_data.prompt_name,
        analysisType: doc.analysis_data.analysisType || doc.analysis_data.analysis_type,
        promptType: doc.analysis_data.promptType || doc.analysis_data.prompt_type,
        templateName: doc.analysis_data.templateName || doc.analysis_data.template_name,
        templateType: doc.analysis_data.templateType || doc.analysis_data.template_type,
        analyzedBy: doc.analysis_data.document_metadata?.analyzed_by || doc.uploaded_by_username || doc.uploaded_by,
        analysisDate: doc.analysis_data.document_metadata?.analysis_timestamp,
        ...doc.analysis_data
      };

      // Export to accessible PDF
      await exportToAccessiblePDF(analysisData, {
        documentName: doc.original_filename
      });

    } catch (error) {
      console.error('Accessible PDF download failed:', error);
      alert('Failed to download accessible PDF. Please try again.');
    } finally {
      setDownloadingPdfFor(null);
    }
  }

  // Handle viewing analysis details from history
  const handleViewAnalysisDetails = async (analysisId: string) => {
    try {
      setViewingAnalysisId(analysisId)
      const detail = await analysisHistoryApi.getAnalysis(
        selectedOrganizationId!,
        selectedWorkspaceId!,
        analysisId,
        true
      )
      setSelectedAnalysisDetail(detail)
      
      // Format the analysis data for display
      if (detail.analysis_data) {
        const username = user?.userInfo?.username || user?.name || userId;
        
        // Extract filenames from analysis data - preserve the array for proper display
        const documentMetadata = detail.analysis_data.document_metadata || {};
        const filenames = documentMetadata.filenames || [];
        const filenameStr = documentMetadata.filename || filenames.join(', ') || `${detail.document_count} documents`;
        const documentTypes = filenames.map((fname: string) =>
          documents.find(d => d.original_filename === fname)?.document_type ?? ''
        )

        // Ensure analysis_data includes document_metadata with filenames
        const enhancedAnalysisData = {
          ...detail.analysis_data,
          document_metadata: {
            ...documentMetadata,
            filenames: filenames,
            document_types: documentTypes
          }
        };
        
        setAnalysisResult({
          status: 'completed',
          filename: filenameStr,
          document_type: 'bulk_analysis',
          duration: 0,
          promptName: detail.analysis_data.promptName || detail.prompt_name,
          analysisType: detail.analysis_data.analysisType || detail.analysis_type,
          promptType: detail.analysis_data.promptType || detail.prompt_type,
          templateName: detail.analysis_data.templateName || detail.template_name,
          templateType: detail.analysis_data.templateType || detail.template_type,
          analyzedBy: detail.created_by || username,
          analysisDate: detail.completed_at,
          documentCount: detail.document_count,
          ...enhancedAnalysisData
        })
      }
      
      setActiveTab('create') // Switch to create tab to show results
      
      // Scroll to top to show the results
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.error('Error fetching analysis details:', error)
      handleApiError(error)
    } finally {
      setViewingAnalysisId(null)
    }
  }

  // Handle clearing the historical view
  const handleClearHistoricalView = () => {
    setSelectedAnalysisDetail(null)
    setAnalysisResult(null)
  }

  // Handle downloading analysis
  const handleDownloadAnalysis = async (analysisId: string) => {
    try {
      const downloadInfo = await analysisHistoryApi.getAnalysisDownloadUrl(
        selectedOrganizationId!,
        selectedWorkspaceId!,
        analysisId
      )

      // Fetch as blob so the download attribute works in Safari (Safari ignores
      // the download attribute on cross-origin URLs like pre-signed S3 URLs)
      const response = await fetch(downloadInfo.download_url)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = downloadInfo.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error downloading analysis:', error)
      handleApiError(error)
    }
  }

  // Handle deleting analysis
  const handleDeleteAnalysis = (analysisId: string) => {
    setAnalysisToDelete(analysisId)
    setDeleteConfirmOpen(true)
  }
  
  const confirmDeleteAnalysis = async () => {
    if (!analysisToDelete) return
    
    try {
      await analysisHistoryApi.deleteAnalysis(
        selectedOrganizationId!,
        selectedWorkspaceId!,
        analysisToDelete
      )
      // Refresh the analyses list
      refetchAnalyses()
      setDeleteConfirmOpen(false)
      setAnalysisToDelete(null)
      setNotification({
        type: 'success',
        message: 'Analysis deleted successfully'
      })
    } catch (error) {
      console.error('Error deleting analysis:', error)
      setDeleteConfirmOpen(false)
      setAnalysisToDelete(null)
      setNotification({
        type: 'error',
        message: 'Failed to delete analysis'
      })
    }
  }

  // Handle downloading analysis as PDF from history
  const handleDownloadAnalysisPDF = async (analysisId: string) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return

    setDownloadingAnalysisPdfFor(analysisId)

    try {

      // Fetch the full analysis with results
      const detail = await analysisHistoryApi.getAnalysis(
        selectedOrganizationId,
        selectedWorkspaceId,
        analysisId,
        true
      )

      if (!detail.analysis_data) {
        alert('No analysis data available to export')
        return
      }

      // Create analysis data object for PDF generation
      const analysisData = {
        status: 'completed',
        filename: `${detail.document_count} documents`,
        original_filename: `${detail.document_count} documents`,
        document_type: 'bulk_analysis',
        documentType: 'bulk_analysis',
        duration: 0,
        promptName: detail.analysis_data.promptName || detail.prompt_name,
        analysisType: detail.analysis_data.analysisType || detail.analysis_type,
        promptType: detail.analysis_data.promptType || detail.prompt_type,
        templateName: detail.analysis_data.templateName || detail.template_name,
        templateType: detail.analysis_data.templateType || detail.template_type,
        analyzedBy: detail.created_by,
        analysisDate: detail.completed_at,
        documentCount: detail.document_count,
        ...detail.analysis_data
      };

      await exportToAccessiblePDF(analysisData, {
        documentName: `Analysis_${analysisId.slice(0, 8)}`
      });

    } catch (error) {
      console.error('Accessible PDF download failed:', error);
      alert('Failed to download accessible PDF. Please try again.');
    } finally {
      setDownloadingAnalysisPdfFor(null);
    }
  }


  return (
    <WorkspaceRequiredGuard
      selectedOrganizationId={selectedOrganizationId}
      selectedWorkspaceId={selectedWorkspaceId}
      title="Workspace Required"
      message="Please select an organization and workspace to view and analyze documents."
    >
    <>
    <ConfirmDialog
      isOpen={deleteConfirmOpen}
      onClose={() => {
        setDeleteConfirmOpen(false)
        setAnalysisToDelete(null)
      }}
      onConfirm={confirmDeleteAnalysis}
      title="Delete Analysis"
      message="Are you sure you want to delete this analysis?"
      confirmText="Delete"
      cancelText="Cancel"
      variant="danger"
    />
    
    {/* Notification Banner */}
    {notification && (
      <div className="fixed top-16 left-0 right-0 z-[60] flex justify-center pt-4 px-4 pointer-events-none">
        <div className={`pointer-events-auto max-w-2xl w-full rounded-lg shadow-lg border ${
          notification.type === 'success' 
            ? 'bg-green-50 border-green-200' 
            : notification.type === 'error'
            ? 'bg-red-50 border-red-200'
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="p-4 flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              {notification.type === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : notification.type === 'error' ? (
                <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${
                notification.type === 'success'
                  ? 'text-green-800'
                  : notification.type === 'error'
                  ? 'text-red-800'
                  : 'text-blue-800'
              }`}>
                {notification.message}
              </p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className={`flex-shrink-0 rounded-lg p-1 transition-colors ${
                notification.type === 'success'
                  ? 'hover:bg-green-100 text-green-600'
                  : notification.type === 'error'
                  ? 'hover:bg-red-100 text-red-600'
                  : 'hover:bg-blue-100 text-blue-600'
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )}
    
    <div className="flex h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Sidebar - Hidden when viewing analysis results or on history tab */}
      {!analysisResult && !selectedAnalysisDetail && analyzingDocuments.length === 0 && activeTab === 'create' && (
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col shadow-lg">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">📁 Folders</h2>
        </div>
        
        <div className="flex-1 overflow-y-auto p-2">
          {/* All Documents */}
          <button
            onClick={() => setSelectedFolder('all')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors mb-1 ${
              selectedFolder === 'all'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <File className="w-5 h-5" />
            <span className="font-medium flex-1 text-left">All Documents</span>
            <span className="text-sm font-semibold">{documents.length}</span>
          </button>

          {/* Folder List */}
          {folders.length > 0 && (
            <>
              <div className="pt-4 pb-2 px-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Courses
                </h3>
              </div>
              
              {folders.map(folder => (
                <button
                  key={folder}
                  onClick={() => setSelectedFolder(folder)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors mb-1 ${
                    selectedFolder === folder
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Folder className="w-5 h-5" />
                  <span className="font-medium flex-1 text-left uppercase">{folder}</span>
                  <span className="text-sm font-semibold">{getFolderCount(folder)}</span>
                </button>
              ))}
            </>
          )}

          {folders.length === 0 && (
            <div className="px-3 py-8 text-center">
              <FolderPlus className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No folders yet</p>
              <p className="text-xs text-gray-400 mt-1">Upload documents with folders to see them here</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-600">
            <div className="flex justify-between mb-1">
              <span>Total Folders:</span>
              <span className="font-semibold">{folders.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Documents:</span>
              <span className="font-semibold">{documents.length}</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="w-full px-6 py-8">
          {/* Header */}
          <div className="bg-white border border-gray-200 p-8 rounded-xl shadow-sm mb-8">
            <h1 className="text-4xl font-bold mb-4 text-gray-900">
              Document Analysis
              {selectedFolder !== 'all' && activeTab === 'create' && !selectedAnalysisDetail && (
                <span className="text-2xl text-blue-600 ml-4">
                  📁 {selectedFolder.toUpperCase()}
                </span>
              )}
            </h1>
            <p className="text-lg text-gray-600 mb-4">
              Analyze documents individually or in bulk, and view your analysis history
            </p>
            
            {activeTab === 'create' && !selectedAnalysisDetail && !analysisResult && (
              <div className="text-sm bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-gray-700"><strong>Step 1:</strong> Upload documents in the Documents tab</p>
                <p className="text-gray-700"><strong>Step 2:</strong> Select document(s) below and click "Analyze"</p>
                <p className="text-gray-700"><strong>Step 3:</strong> View the detailed analysis report</p>
              </div>
            )}
          </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => {
                  setActiveTab('create')
                  if (selectedAnalysisDetail) {
                    handleClearHistoricalView()
                  }
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'create'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Create New Analysis
                {selectedAnalysisDetail && activeTab === 'create' && (
                  <span className="ml-2 text-xs text-gray-500">
                    (Viewing: {selectedAnalysisDetail.analysis_id.slice(0, 8)}...)
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  setActiveTab('history')
                  // Clear any active analysis result and detail when switching to history tab
                  setAnalysisResult(null)
                  setSelectedAnalysisDetail(null)
                  resetIndividualAnalysisMode()
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'history'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Analysis History
                {analysesData && analysesData.count > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-blue-800 bg-blue-100 rounded-full">
                    {analysesData.count}
                  </span>
                )}
              </button>
            </nav>
          </div>
        </div>

        {/* Job Poller Component */}
        {currentJobId && (
          <JobPoller
            jobId={currentJobId}
            jobContext={{
              org_id: selectedOrganizationId || undefined,
              ws_id: selectedWorkspaceId || undefined
            }}
            onStatusChange={setJobStatus}
            onComplete={handleJobComplete}
            onError={handleJobError}
          />
        )}

        {/* Analysis Loading State with Job Status */}
        {analyzingDocuments.length > 0 && (
          <div className="mb-8">
            <JobStatus
              jobId={currentJobId}
              jobStatus={jobStatus}
              isProcessing={analyzingDocuments.length > 0}
              processDescription={`Analyzing ${analyzingDocuments.length} document${analyzingDocuments.length > 1 ? 's' : ''}...`}
            />
          </div>
        )}

        {/* Analysis Result Display */}
        {analysisResult && analyzingDocuments.length === 0 && (
          <div className="mb-8">
            {/* Back to Analysis Button */}
            <div className="mb-4">
              <button
                onClick={() => {
                  // Always go back to main analysis page (create tab)
                  setAnalysisResult(null)
                  setSelectedDocuments([])
                  setAnalyzingDocuments([])
                  setCurrentJobId(null)
                  setJobStatus(null)
                  setSelectedAnalysisDetail(null)
                  resetIndividualAnalysisMode()
                  analyzeMutation.reset()
                  setActiveTab('create')
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Analysis
              </button>
            </div>
            <AnalysisReport analysisData={analysisResult} />
          </div>
        )}

        {/* History Tab Content */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Analysis History</h2>
              <AnalysisHistoryTable
                analyses={analysesData?.analyses || []}
                onViewDetails={handleViewAnalysisDetails}
                onDelete={handleDeleteAnalysis}
                onDownload={handleDownloadAnalysis}
                onDownloadPDF={handleDownloadAnalysisPDF}
                isLoading={analysesLoading}
                loadingAnalysisId={viewingAnalysisId || downloadingAnalysisPdfFor}
                loadingAction={viewingAnalysisId ? 'view' : downloadingAnalysisPdfFor ? 'pdf' : null}
              />
            </div>
          </div>
        )}

        {/* Document Selection and Analysis - Only show on create tab */}
        {activeTab === 'create' && !analysisResult && analyzingDocuments.length === 0 && (
          <div>
            {documentsLoading ? (
              <div className="flex justify-center items-center py-20">
                <div className="text-center">
                  <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-lg text-gray-600">Loading documents...</p>
                </div>
              </div>
            ) : documents.length === 0 ? (
              <div className="bg-white p-12 rounded-xl shadow-lg text-center">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">No Documents Found</h2>
                <p className="text-gray-600 mb-4">Please upload documents in the Documents tab first.</p>
                <a href="/#/documents" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
                  Go to Documents Tab
                </a>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Document Selection */}
                <div className="bg-white p-8 rounded-xl shadow-lg">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">
                      {selectedFolder === 'all' ? 'Select Document to Analyze' : `${selectedFolder.toUpperCase()} Documents`}
                    </h2>
                    
                    {/* Search Bar */}
                    {allDocuments.length > 0 && (
                      <div className="relative w-96">
                        <label htmlFor="document-search" className="sr-only">
                          Search documents
                        </label>
                        <input
                          id="document-search"
                          type="text"
                          placeholder="Search documents..."
                          value={searchQuery}
                          onChange={(e) => handleSearchChange(e.target.value)}
                          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {searchQuery && (
                          <button
                            onClick={() => handleSearchChange('')}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* All Documents */}
                  {allDocuments.length > 0 && (
                    <div className="mb-8">
                      {/* Pagination Controls - Top */}
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-4">
                          {/* Results count */}
                          <div className="text-sm text-gray-600">
                            {searchQuery ? (
                              <>Found {sortedDocuments.length} of {allDocuments.length} documents</>
                            ) : (
                              <>Showing {startIndex + 1}-{Math.min(endIndex, totalDocuments)} of {totalDocuments} documents</>
                            )}
                          </div>
                        </div>

                        {/* Page size selector */}
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600">Rows per page:</label>
                          <select
                            value={pageSize}
                            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                                Select
                              </th>
                              <th 
                                onClick={() => handleSort('filename')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                <div className="flex items-center gap-2">
                                  Filename
                                  <SortIcon column="filename" />
                                </div>
                              </th>
                              <th 
                                onClick={() => handleSort('type')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                <div className="flex items-center gap-2">
                                  Type
                                  <SortIcon column="type" />
                                </div>
                              </th>
                              <th
                                onClick={() => handleSort('status')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                <div className="flex items-center gap-2">
                                  Status
                                  <SortIcon column="status" />
                                </div>
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                Actions
                              </th>
                              <th 
                                onClick={() => handleSort('analyzed_by')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                <div className="flex items-center gap-2">
                                  Analyzed By
                                  <SortIcon column="analyzed_by" />
                                </div>
                              </th>
                              <th 
                                onClick={() => handleSort('analyzed_date')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                <div className="flex items-center gap-2">
                                  Analyzed Date
                                  <SortIcon column="analyzed_date" />
                                </div>
                              </th>
                              <th
                                onClick={() => handleSort('size')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                <div className="flex items-center gap-2">
                                  Size
                                  <SortIcon column="size" />
                                </div>
                              </th>
                               <th
                                onClick={() => handleSort('uploaded')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                <div className="flex items-center gap-2">
                                  Uploaded
                                  <SortIcon column="uploaded" />
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedDocuments.map((doc) => {
                              const isAnalyzed = doc.processing_status === 'completed'
                              const isSelected = selectedDocuments.includes(doc.document_id)
                              // Check if token estimation is in progress or missing
                              const isTokenEstimationPending = doc.token_estimation_status === 'processing' || 
                                (doc.file_size_bytes > 10 * 1024 * 1024 && !doc.estimated_token_count)
                              const isDisabled = isTokenEstimationPending
                              
                              return (
                                <tr 
                                  key={doc.document_id}
                                  onClick={() => {
                                    if (isDisabled) return; // Don't allow selection if disabled
                                    setSelectedDocuments(prev => 
                                      prev.includes(doc.document_id)
                                        ? prev.filter(id => id !== doc.document_id)
                                        : [...prev, doc.document_id]
                                    )
                                  }}
                                  className={`transition-colors ${
                                    isDisabled 
                                      ? 'bg-gray-100 cursor-not-allowed opacity-60' 
                                      : isSelected 
                                        ? 'bg-blue-50 cursor-pointer' 
                                        : 'hover:bg-gray-50 cursor-pointer'
                                  }`}
                                  title={isDisabled ? 'Token estimation in progress - please wait' : ''}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                                      isDisabled
                                        ? 'border-gray-400 bg-gray-200 cursor-not-allowed'
                                        : isSelected
                                          ? 'bg-blue-500 border-blue-500'
                                          : 'border-gray-300'
                                    }`}>
                                      {isSelected && !isDisabled && (
                                        <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                      {isDisabled && (
                                        <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                        </svg>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <FileText className="w-5 h-5 text-blue-500 mr-3" />
                                      <span className="text-sm font-medium text-gray-900">{doc.original_filename}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-700 capitalize">{doc.document_type}</span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(doc.processing_status)}`}>
                                        {getStatusText(doc.processing_status)}
                                      </span>
                                      {isAnalyzed && (
                                        <CheckCircle className="h-4 w-4 text-green-600" title="Previously Analyzed" />
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-1">
                                      {isAnalyzed ? (
                                        <>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (doc.analysis_data) {
                                                setAnalysisResult({
                                                  status: 'completed',
                                                  filename: doc.original_filename,
                                                  document_type: doc.document_type,
                                                  duration: 0,
                                                  promptName: doc.analysis_data.promptName || doc.analysis_data.prompt_name,
                                                  analysisType: doc.analysis_data.analysisType || doc.analysis_data.analysis_type,
                                                  promptType: doc.analysis_data.promptType || doc.analysis_data.prompt_type,
                                                  templateName: doc.analysis_data.templateName || doc.analysis_data.template_name,
                                                  templateType: doc.analysis_data.templateType || doc.analysis_data.template_type,
                                                  analyzedBy: doc.analysis_data.document_metadata?.analyzed_by || doc.uploaded_by_username || doc.uploaded_by,
                                                  analysisDate: doc.analysis_data.document_metadata?.analysis_timestamp,
                                                  ...doc.analysis_data
                                                })
                                              }
                                            }}
                                            className="p-1 hover:bg-blue-100 rounded transition-colors"
                                            title="View Analysis"
                                          >
                                            <Eye className="h-4 w-4 text-blue-600" />
                                          </button>
                                          <button
                                            onClick={(e) => handleDownloadPDF(doc, e)}
                                            disabled={downloadingPdfFor === doc.document_id}
                                            className="p-1 hover:bg-green-100 rounded transition-colors disabled:opacity-50"
                                            title="Download accessible PDF report"
                                            aria-label={`Download accessible PDF analysis report for ${doc.original_filename}`}
                                          >
                                            {downloadingPdfFor === doc.document_id ? (
                                              <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
                                            ) : (
                                              <Download className="h-4 w-4 text-green-600" />
                                            )}
                                          </button>
                                        </>
                                      ) : (
                                        <span className="text-xs text-gray-400">-</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-700">
                                      {doc.analysis_data?.document_metadata?.analyzed_by || doc.uploaded_by_username || doc.uploaded_by || '-'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-700">
                                      {doc.analysis_data?.document_metadata?.analysis_timestamp
                                        ? formatInTimeZone(new Date(doc.analysis_data.document_metadata.analysis_timestamp), 'America/New_York', 'MMM d, yyyy h:mm a')
                                        : '-'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-700">{(doc.file_size_bytes / 1024 / 1024).toFixed(2)} MB</span>
                                  </td>
                                   <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-700">{format(new Date(doc.uploaded_at), 'MMM d, yyyy')}</span>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>

                      {sortedDocuments.length === 0 && searchQuery && (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No documents found matching "{searchQuery}"</p>
                        </div>
                      )}

                      {/* Pagination Controls - Bottom */}
                      {totalPages > 1 && (
                        <div className="flex justify-between items-center pt-4 border-t mt-4">
                          <div className="text-sm text-gray-600">
                            Page {currentPage} of {totalPages}
                          </div>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={handlePreviousPage}
                              disabled={currentPage === 1}
                              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                            >
                              ← Previous
                            </button>
                            <button
                              onClick={handleNextPage}
                              disabled={currentPage === totalPages}
                              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                            >
                              Next →
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Token Warning Banner */}
                      {showTokenWarning && !individualAnalysisMode && selectedDocuments.length >= 2 && (
                        <div className="mt-6 border-t pt-6">
                          <div className="bg-white p-6 rounded-xl shadow-lg">
                            <div className="flex items-start gap-4">
                              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                              <div className="flex-1">
                                <h3 className="text-xl font-bold text-gray-800 mb-2">
                                  Token Limit Warning
                                </h3>
                                <p className="text-gray-700 mb-4">
                                  Your document selection exceeds the recommended 100,000 token limit for optimal analysis.
                                  Large document sets may produce less accurate results when analyzed together.
                                </p>
                                
                                {/* Token Stats */}
                                <div className="flex flex-wrap items-center gap-3 text-sm mb-6">
                                  <span className="text-gray-700">Total:</span>
                                  <span className="px-3 py-1.5 rounded-md bg-red-100 text-red-900 font-semibold">
                                    {calculateTotalTokens.toLocaleString()} tokens
                                  </span>
                                  <span className="text-gray-600 mx-1">|</span>
                                  <span className="text-gray-700">Limit: {SAFE_TOKEN_LIMIT.toLocaleString()} tokens</span>
                                </div>
                                
                                <div className="space-y-4">
                                  <div>
                                    <p className="text-sm font-medium text-gray-800 mb-3">Choose how to proceed:</p>
                                    <p className="text-sm text-gray-600 italic mb-4">
                                      You can either reduce your selection manually to stay within the token limit, or use our intelligent analysis mode that analyzes each document individually first, then applies detailed analysis on the summaries.
                                    </p>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-3">
                                    {/* Manual Selection Option */}
                                    <button
                                      onClick={() => {
                                        setShowTokenWarning(false)
                                      }}
                                      className="p-4 text-left border-2 border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all"
                                    >
                                      <div className="font-medium text-gray-900 mb-1">Manual Selection</div>
                                      <div className="text-xs text-gray-600">Remove documents to stay within the 100,000 token limit</div>
                                    </button>
                                    
                                    {/* Individual Analysis Option */}
                                    <button
                                      onClick={startIndividualAnalysis}
                                      disabled={isAnalyzingIndividually}
                                      className="p-4 text-left border-2 border-blue-500 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <div className="font-medium text-blue-900 mb-1 flex items-center gap-2">
                                        {isAnalyzingIndividually ? (
                                          <>
                                            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                                            Analyzing...
                                          </>
                                        ) : (
                                          'Individual Analysis Mode'
                                        )}
                                      </div>
                                      <div className="text-xs text-blue-700">
                                        {isAnalyzingIndividually 
                                          ? 'Starting analysis for each document...' 
                                          : 'Analyze each document individually, then apply detailed analysis (Recommended for large documents)'}
                                      </div>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Individual Analysis Mode */}
                      {individualAnalysisMode && (
                        <div className="mt-6 border-t pt-6">
                          <div className="space-y-6 mb-8">
                            {/* Mode indicator */}
                            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                    Individual Analysis Mode
                                  </h3>
                                  <p className="text-sm text-gray-700">
                                    Each document is being analyzed separately. Once complete, you can apply detailed analysis.
                                  </p>
                                </div>
                                {!isAnalyzingIndividually && (
                                  <button
                                    onClick={resetIndividualAnalysisMode}
                                    className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                                  >
                                    Exit Mode
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Analysis Tray */}
                            <IndividualAnalysisTray
                              title="Document Analyses"
                              analyses={individualAnalyses}
                              isProcessing={isAnalyzingIndividually}
                            />

                            {/* Analyze button */}
                            {!isAnalyzingIndividually && (
                              <div className="flex justify-center">
                                <button
                                  onClick={analyzeFromUnifiedSummaries}
                                  disabled={
                                    analyzeMutation.isPending ||
                                    Array.from(individualAnalyses.values()).filter(a => a.status === 'completed').length === 0 ||
                                    !memberHasRequiredDefaults
                                  }
                                  className="bg-[#e21833] text-white px-8 py-4 rounded-lg hover:bg-[#c41429] font-medium text-lg shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={!memberHasRequiredDefaults && !isAdmin ? 'Analysis defaults not configured for members' : ''}
                                >
                                  {analyzeMutation.isPending ? (
                                    <div className="flex items-center gap-2">
                                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                                      Analyzing...
                                    </div>
                                  ) : (
                                    'Analyze All Documents'
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Prompt Configuration - Only show for admins OR show member message */}
                      {selectedDocuments.length > 0 && !individualAnalysisMode && (
                        <div className="mt-6 border-t pt-6">
                          <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            Analysis Configuration 
                            {selectedDocuments.length > 1 && (
                              <span className="text-sm text-blue-600 ml-2">
                                (Bulk Analysis: {selectedDocuments.length} documents)
                              </span>
                            )}
                          </h3>

                          {/* Member view - show configured defaults or warning */}
                          {!isAdmin && (
                            <div className={`p-4 rounded-lg border mb-4 ${
                              memberHasRequiredDefaults
                                ? 'bg-blue-50 border-blue-200'
                                : 'bg-yellow-50 border-yellow-300'
                            }`}>
                              {memberHasRequiredDefaults ? (
                                <div>
                                  <h4 className="font-semibold text-blue-900 mb-2">
                                    Using Workspace Default Configuration
                                  </h4>
                                  <div className="text-sm text-blue-800 space-y-1">
                                    <p>
                                      <strong>Prompt:</strong> {effectivePromptName || 'Not configured'} 
                                      <span className="ml-2 text-xs">({effectivePromptType})</span>
                                    </p>
                                    <p>
                                      <strong>Template:</strong> {effectiveTemplateName || 'Not configured'}
                                      <span className="ml-2 text-xs">({effectiveTemplateType})</span>
                                    </p>
                                    <p className="mt-2 text-xs text-blue-700">
                                      These defaults are configured by your workspace administrator.
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <h4 className="font-semibold text-yellow-900 mb-2">
                                    ⚠️ Configuration Required
                                  </h4>
                                  <p className="text-sm text-yellow-800">
                                    {isBulkAnalysis 
                                      ? 'Bulk analysis defaults have not been configured for members. Please contact your workspace administrator.'
                                      : 'Single document analysis defaults have not been configured for members. Please contact your workspace administrator.'}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Admin view - show full configuration UI */}
                          {isAdmin && !showTokenWarning && (
                          <>
                          <div className="space-y-4">
                            {/* Prompt Type Selection */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Prompt Type
                              </label>
                              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                                <button
                                  onClick={() => {
                                    setPromptType('default')
                                    setSelectedPrompt(null)
                                  }}
                                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    promptType === 'default'
                                      ? 'bg-white text-blue-600 shadow-sm'
                                      : 'text-gray-600 hover:text-gray-900'
                                  }`}
                                >
                                  Default
                                </button>
                                <button
                                  onClick={() => {
                                    setPromptType('workspace')
                                    setSelectedPrompt(null)
                                  }}
                                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    promptType === 'workspace'
                                      ? 'bg-white text-blue-600 shadow-sm'
                                      : 'text-gray-600 hover:text-gray-900'
                                  }`}
                                >
                                  Workspace
                                </button>
                              </div>
                            </div>

                            {/* Prompt List */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Prompt
                              </label>
                              <div className="bg-gray-50 rounded-lg p-4 max-h-80 overflow-y-auto">
                                <PromptsList
                                  level={promptType}
                                  selectedItem={selectedPrompt}
                                  onSelectItem={(promptPath) => {
                                    // Extract prompt name and analysis type
                                    const promptName = extractPromptName(promptPath);
                                    const analysisType = extractAnalysisType(promptPath);
                                    setSelectedPrompt({ name: promptName, analysisType });
                                  }}
                                  prompts={availablePrompts}
                                  isLoading={promptsLoading}
                                />
                              </div>
                              {availablePrompts.length === 0 && !promptsLoading && (
                                <p className="text-xs text-gray-500 mt-2">
                                  No prompts available for {promptType} configuration
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Template Configuration — hidden for "other" document type */}
                          {!hasOtherDocument && <div className="mt-6 pt-6 border-t">
                            <h4 className="text-md font-semibold text-gray-800 mb-4">Template Configuration</h4>

                            <div className="space-y-4">
                              {/* Template Type Selection */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Template Type
                                </label>
                                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                                  <button
                                    onClick={() => {
                                      setTemplateType('default')
                                      setSelectedTemplate(null)
                                    }}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                      templateType === 'default'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                  >
                                    Default
                                  </button>
                                  <button
                                    onClick={() => {
                                      setTemplateType('workspace')
                                      setSelectedTemplate(null)
                                    }}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                      templateType === 'workspace'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                  >
                                    Workspace
                                  </button>
                                </div>
                              </div>

                              {/* Template List */}
                              {templatesLoading ? (
                                <div className="text-center py-4">
                                  <p className="text-sm text-gray-500">Loading templates...</p>
                                </div>
                              ) : (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Template Schema
                                  </label>
                                  <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                                    {availableTemplates.length > 0 ? (
                                      <div className="space-y-2">
                                        {availableTemplates.map((template) => (
                                          <button
                                            key={template}
                                            onClick={() => setSelectedTemplate(template)}
                                            className={`w-full text-left px-3 py-2 rounded-md transition-colors border ${
                                              selectedTemplate === template
                                                ? 'bg-blue-100 text-blue-800 border-blue-300'
                                                : 'bg-white hover:bg-gray-50 border-gray-200'
                                            }`}
                                          >
                                            <div className="font-medium">{template}</div>
                                          </button>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-500">
                                        No templates available for {templateType} configuration
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>}
                          </>
                          )}
                        </div>
                      )}

                      {/* Analysis Button */}
                      {selectedDocuments.length > 0 && !individualAnalysisMode && (
                        <div className="mt-6 border-t pt-6">
                          {(() => {
                            const selectedDocs = allDocuments.filter(d => selectedDocuments.includes(d.document_id))
                            const allAnalyzed = selectedDocs.every(d => d.processing_status === 'completed')

                            return (
                              <>
                                {showTokenWarning && (
                                  <div className="mb-4 bg-red-50 border border-red-200 p-4 rounded-lg">
                                    <div className="flex items-center gap-2">
                                      <AlertCircle className="w-5 h-5 text-red-600" />
                                      <p className="text-sm text-red-800 font-medium">
                                        Analysis disabled: Selection exceeds {SAFE_TOKEN_LIMIT.toLocaleString()} token limit ({calculateTotalTokens.toLocaleString()} tokens selected)
                                      </p>
                                    </div>
                                    <p className="text-xs text-red-700 mt-2">
                                      Please reduce your selection or use Individual Analysis Mode above.
                                    </p>
                                  </div>
                                )}
                                <button
                                  onClick={() => {
                                    const docs = allDocuments.filter(d => selectedDocuments.includes(d.document_id))
                                    if (docs.length > 0) analyzeMutation.mutate(docs)
                                  }}
                                  disabled={analyzeMutation.isPending || analyzingDocuments.length > 0 || !memberHasRequiredDefaults || showTokenWarning}
                                  className="w-full bg-[#e21833] text-white font-bold py-4 px-6 rounded-lg hover:bg-[#c41429] focus:outline-none focus:ring-2 focus:ring-[#e21833] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                  title={
                                    showTokenWarning 
                                      ? 'Selection exceeds token limit' 
                                      : (!memberHasRequiredDefaults && !isAdmin ? 'Analysis defaults not configured for members' : '')
                                  }
                                >
                                  {analyzeMutation.isPending || analyzingDocuments.length > 0 ? (
                                    <div className="flex items-center justify-center">
                                      <div className="animate-spin h-6 w-6 border-3 border-white border-t-transparent rounded-full mr-3"></div>
                                      Analyzing...
                                    </div>
                                  ) : (
                                    <>{selectedDocuments.length === 1 ? 'Analyze' : `Analyze ${selectedDocuments.length} Documents`}</>
                                  )}
                                </button>

                                {allAnalyzed && selectedDocuments.length === 1 && analyzingDocuments.length === 0 && (
                                  <div className="mt-3 flex gap-2">
                                    <button
                                      onClick={() => {
                                        // Show the existing analysis with prompt/template info
                                        const selectedDoc = selectedDocs[0]
                                        if (selectedDoc.analysis_data) {
                                          setAnalysisResult({
                                            status: 'completed',
                                            filename: selectedDoc.original_filename,
                                            document_type: selectedDoc.document_type,
                                            duration: 0,
                                            // Include prompt and template information if they exist in analysis_data
                                            promptName: selectedDoc.analysis_data.promptName || selectedDoc.analysis_data.prompt_name,
                                            analysisType: selectedDoc.analysis_data.analysisType || selectedDoc.analysis_data.analysis_type,
                                            promptType: selectedDoc.analysis_data.promptType || selectedDoc.analysis_data.prompt_type,
                                            templateName: selectedDoc.analysis_data.templateName || selectedDoc.analysis_data.template_name,
                                            templateType: selectedDoc.analysis_data.templateType || selectedDoc.analysis_data.template_type,
                                            analyzedBy: selectedDoc.analysis_data.document_metadata?.analyzed_by || selectedDoc.uploaded_by_username || selectedDoc.uploaded_by,
                                            analysisDate: selectedDoc.analysis_data.document_metadata?.analysis_timestamp,
                                            ...selectedDoc.analysis_data
                                          })
                                        }
                                      }}
                                      className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
                                    >
                                      View Existing Analysis
                                    </button>
                                  </div>
                                )}

                                {analyzeMutation.isError && (
                                  <div className="mt-4 bg-red-50 border border-red-200 rounded p-4">
                                    <p className="text-red-600 text-sm">
                                      Analysis failed: {analyzeMutation.error?.message || 'Unknown error'}
                                    </p>
                                  </div>
                                )}
                              </>
                            )
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* No documents message */}
                  {allDocuments.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-600">No documents available for analysis.</p>
                      <p className="text-gray-500 text-sm">Upload documents in the Documents tab first.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

          {/* Back to results button */}
          {analysisResult && analyzingDocuments.length === 0 && (
            <div className="mt-8 text-center">
              <button
                onClick={() => {
                  setAnalysisResult(null)
                  setSelectedDocuments([])
                  setAnalyzingDocuments([])
                  setCurrentJobId(null)
                  setJobStatus(null)
                  resetIndividualAnalysisMode()
                  analyzeMutation.reset()
                }}
                className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Analyze Another Document
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
    </WorkspaceRequiredGuard>
  )
}